import React, { useEffect, useMemo, useState, createElement } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { LayoutDashboard, Users, MessageSquare, Shield, Bell, LogOut, Search, CheckCircle2, AlertCircle, Clock, Send, Plus, Trash2, Lock, Mail, User, Activity, CreditCard, Menu, X, ArrowLeft, Power, Ban, Package, Tag, UserCheck, Bot, Settings, Eye, Paperclip, Download, Upload } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { useToast } from '../components/ui/Toast';
import { useLanguage } from '../hooks/useLanguage';
import { supabase, dbUsers, dbProducts, dbOffers, dbCustomers, dbSupportTickets, dbTicketReplies, dbAdminStaff, dbUserNotifications, SupabaseUser, SupabaseTicket, SupabaseTicketReply, SupabaseAdminStaff } from '../utils/supabase';
import { compressImageToBase64 } from '../utils/api';
import { dbActivityLog, ActivityLogEntry, logActivity } from '../utils/activityLog';
// --- Types ---
type AdminSection = 'overview' | 'users' | 'tickets' | 'staff' | 'notifications';
interface AdminSession {
  email: string;
  name: string;
  role: 'super_admin' | 'admin' | 'support';
  permissions: string[];
  loginAt: number;
  authUid: string;
}
// Approved admin emails - only these can access the admin dashboard
const APPROVED_ADMIN_EMAILS = ['wael@operines.ai'];
const SUPER_ADMIN_EMAIL = 'wael@operines.ai';
export function AdminDashboardPage() {
  const {
    language
  } = useLanguage();
  const {
    showToast
  } = useToast();
  const isRTL = language === 'ar';
  // --- Auth State ---
  const [adminUser, setAdminUser] = useState<AdminSession | null>(null);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  // --- Dashboard State ---
  const [activeSection, setActiveSection] = useState<AdminSection>('overview');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(false);
  // --- Data State ---
  const [users, setUsers] = useState<SupabaseUser[]>([]);
  const [tickets, setTickets] = useState<SupabaseTicket[]>([]);
  const [staff, setStaff] = useState<SupabaseAdminStaff[]>([]);
  // --- Search & Filter State ---
  const [userSearch, setUserSearch] = useState('');
  const [ticketStatusFilter, setTicketStatusFilter] = useState<string>('all');
  // --- User Detail State ---
  const [selectedUser, setSelectedUser] = useState<SupabaseUser | null>(null);
  const [userProducts, setUserProducts] = useState<any[]>([]);
  const [userOffers, setUserOffers] = useState<any[]>([]);
  const [userCustomers, setUserCustomers] = useState<any[]>([]);
  const [userTickets, setUserTickets] = useState<SupabaseTicket[]>([]);
  const [userActivities, setUserActivities] = useState<ActivityLogEntry[]>([]);
  const [isLoadingUserDetails, setIsLoadingUserDetails] = useState(false);
  const [isTogglingUserBot, setIsTogglingUserBot] = useState(false);
  const [isTogglingUserSuspend, setIsTogglingUserSuspend] = useState(false);
  const [isDeletingUser, setIsDeletingUser] = useState(false);
  const [isImportingUsers, setIsImportingUsers] = useState(false);
  // --- Notification State ---
  const [notifEmail, setNotifEmail] = useState('');
  const [notifTitleEn, setNotifTitleEn] = useState('');
  const [notifTitleAr, setNotifTitleAr] = useState('');
  const [notifMessageEn, setNotifMessageEn] = useState('');
  const [notifMessageAr, setNotifMessageAr] = useState('');
  const [notifType, setNotifType] = useState<'info' | 'warning' | 'success'>('info');
  const [isSendingNotif, setIsSendingNotif] = useState(false);
  // --- Staff State ---
  const [isStaffModalOpen, setIsStaffModalOpen] = useState(false);
  const [staffName, setStaffName] = useState('');
  const [staffEmail, setStaffEmail] = useState('');
  const [staffRole, setStaffRole] = useState<'admin' | 'support'>('support');
  const [staffPermissions, setStaffPermissions] = useState<string[]>([]);
  const [isAddingStaff, setIsAddingStaff] = useState(false);
  // --- Ticket Detail State ---
  const [selectedTicket, setSelectedTicket] = useState<SupabaseTicket | null>(null);
  const [ticketReplies, setTicketReplies] = useState<SupabaseTicketReply[]>([]);
  const [replyMessage, setReplyMessage] = useState('');
  const [isReplying, setIsReplying] = useState(false);
  const [attachmentBase64, setAttachmentBase64] = useState<string | null>(null);
  // --- Admin Create Ticket State ---
  const [isCreateTicketOpen, setIsCreateTicketOpen] = useState(false);
  const [createTicketSubject, setCreateTicketSubject] = useState('');
  const [createTicketMessage, setCreateTicketMessage] = useState('');
  const [createTicketPriority, setCreateTicketPriority] = useState<'low' | 'medium' | 'high'>('medium');
  const [isCreatingTicket, setIsCreatingTicket] = useState(false);
  // --- Check Auth on Mount ---
  useEffect(() => {
    let subscription: {
      unsubscribe: () => void;
    } | null = null;
    const initAuth = async () => {
      try {
        // Check existing Supabase Auth session
        const {
          data: {
            session
          }
        } = await supabase.auth.getSession();
        if (session?.user) {
          const email = session.user.email?.toLowerCase() || '';
          // Verify email is in approved list
          if (!APPROVED_ADMIN_EMAILS.includes(email)) {
            await supabase.auth.signOut();
            setIsCheckingAuth(false);
            return;
          }
          // Verify email exists in admin_staff with active status
          const staffUser = await dbAdminStaff.getByEmail(email);
          if (staffUser && staffUser.is_active) {
            const isSuperAdmin = email === SUPER_ADMIN_EMAIL.toLowerCase();
            setAdminUser({
              email: staffUser.email,
              name: staffUser.name,
              role: isSuperAdmin ? 'super_admin' : staffUser.role as any,
              permissions: isSuperAdmin ? ['all'] : staffUser.permissions || [],
              loginAt: Date.now(),
              authUid: session.user.id
            });
          } else {
            // Email not in admin_staff or inactive - sign out
            await supabase.auth.signOut();
          }
        }
      } catch (error) {
        console.error('Auth init error:', error);
      }
      setIsCheckingAuth(false);
    };
    initAuth();
    // Listen for auth state changes
    const {
      data: {
        subscription: sub
      }
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_OUT') {
        setAdminUser(null);
        localStorage.removeItem('operines_admin_session');
      }
    });
    subscription = sub;
    return () => {
      subscription?.unsubscribe();
    };
  }, []);
  // --- Load Data when Auth'd ---
  useEffect(() => {
    if (adminUser) {
      loadDashboardData();
    }
  }, [adminUser]);
  const loadDashboardData = async () => {
    setIsLoadingData(true);
    try {
      const [usersData, ticketsData, staffData] = await Promise.all([dbUsers.getAll(), dbSupportTickets.getAllTickets(), dbAdminStaff.getAll()]);
      setUsers(usersData);
      setTickets(ticketsData);
      setStaff(staffData);
    } catch (error) {
      console.error('Failed to load admin data:', error);
      showToast('error', isRTL ? 'فشل تحميل البيانات' : 'Failed to load data');
    } finally {
      setIsLoadingData(false);
    }
  };
  // --- Handlers ---
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!loginEmail || !loginPassword) return;
    setIsLoggingIn(true);
    try {
      const email = loginEmail.toLowerCase().trim();
      // Block non-approved emails immediately
      if (!APPROVED_ADMIN_EMAILS.includes(email)) {
        showToast('error', isRTL ? 'غير مصرح بالدخول' : 'Access denied');
        setIsLoggingIn(false);
        return;
      }
      // Authenticate via Supabase Auth
      const {
        data: authData,
        error: authError
      } = await supabase.auth.signInWithPassword({
        email,
        password: loginPassword
      });
      if (authError || !authData.user) {
        showToast('error', isRTL ? 'بيانات الدخول غير صحيحة' : 'Invalid credentials');
        setIsLoggingIn(false);
        return;
      }
      // Verify user exists in admin_staff table with active status
      const staffUser = await dbAdminStaff.getByEmail(email);
      if (!staffUser || !staffUser.is_active) {
        // Authenticated but not an approved admin - sign out
        await supabase.auth.signOut();
        showToast('error', isRTL ? 'غير مصرح بالدخول كمدير' : 'Not authorized as admin');
        setIsLoggingIn(false);
        return;
      }
      const isSuperAdmin = email === SUPER_ADMIN_EMAIL.toLowerCase();
      const session: AdminSession = {
        email: staffUser.email,
        name: staffUser.name,
        role: isSuperAdmin ? 'super_admin' : staffUser.role as any,
        permissions: isSuperAdmin ? ['all'] : staffUser.permissions || [],
        loginAt: Date.now(),
        authUid: authData.user.id
      };
      setAdminUser(session);
      // Log login activity
      logActivity.adminAction(staffUser.email, `${isSuperAdmin ? 'Super Admin' : 'Staff'} Login`, staffUser.name).catch(console.error);
      showToast('success', isRTL ? 'تم تسجيل الدخول' : 'Login successful');
    } catch (error) {
      console.error('Login error:', error);
      showToast('error', isRTL ? 'حدث خطأ' : 'An error occurred');
    } finally {
      setIsLoggingIn(false);
    }
  };
  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
    } catch (error) {
      console.error('Logout error:', error);
    }
    setAdminUser(null);
    localStorage.removeItem('operines_admin_session');
  };
  const handleTicketClick = async (ticket: SupabaseTicket) => {
    setSelectedTicket(ticket);
    try {
      const replies = await dbTicketReplies.getByTicket(ticket.id!);
      setTicketReplies(replies);
    } catch (error) {
      console.error('Failed to load replies:', error);
    }
  };
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const base64 = await compressImageToBase64(file);
      setAttachmentBase64(base64);
    } catch (error) {
      console.error('Failed to compress image:', error);
      showToast('error', isRTL ? 'فشل تحميل الصورة' : 'Failed to load image');
    }
  };
  const renderMessageContent = (msg: string) => {
    const parts = msg.split('[ATTACHMENT:');
    if (parts.length === 1) return <p className="whitespace-pre-wrap" data-id="element-874">{msg}</p>;
    const textPart = parts[0].trim();
    const attachmentPart = parts[1].split(']')[0];
    return <div className="space-y-2" data-id="element-875">
        {textPart && <p className="whitespace-pre-wrap" data-id="element-876">{textPart}</p>}
        {attachmentPart && <img src={attachmentPart} alt="Attachment" className="max-w-full h-auto rounded-lg border border-white/20 max-h-64 object-contain" data-id="element-877" />}
      </div>;
  };
  const handleReplyTicket = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedTicket || !replyMessage.trim() && !attachmentBase64 || !adminUser) return;
    setIsReplying(true);
    try {
      let finalMessage = replyMessage;
      if (attachmentBase64) {
        finalMessage += `\n\n[ATTACHMENT:${attachmentBase64}]`;
      }
      const reply = await dbTicketReplies.create({
        ticket_id: selectedTicket.id!,
        from_type: 'admin',
        from_name: adminUser.name,
        message: finalMessage
      });
      if (reply) {
        setTicketReplies([...ticketReplies, reply]);
        setReplyMessage('');
        setAttachmentBase64(null);
        // Notify user
        await dbUserNotifications.create({
          user_email: selectedTicket.user_email,
          title: 'New reply on your ticket',
          title_ar: 'رد جديد على تذكرتك',
          message: `Admin has replied to your ticket: ${selectedTicket.subject}`,
          message_ar: `قام فريق الدعم بالرد على تذكرتك: ${selectedTicket.subject}`,
          type: 'info',
          read: false,
          sent_by: 'System'
        });
        // Update ticket status to in_progress if it was open
        if (selectedTicket.status === 'open') {
          await dbSupportTickets.updateStatus(selectedTicket.id!, 'in_progress');
          setSelectedTicket({
            ...selectedTicket,
            status: 'in_progress'
          });
          setTickets(tickets.map(t => t.id === selectedTicket.id ? {
            ...t,
            status: 'in_progress'
          } : t));
        }
        showToast('success', isRTL ? 'تم إرسال الرد' : 'Reply sent');
      }
    } catch (error) {
      console.error('Reply error:', error);
      showToast('error', isRTL ? 'فشل إرسال الرد' : 'Failed to send reply');
    } finally {
      setIsReplying(false);
    }
  };
  const handleUpdateTicketStatus = async (status: SupabaseTicket['status']) => {
    if (!selectedTicket) return;
    try {
      const success = await dbSupportTickets.updateStatus(selectedTicket.id!, status);
      if (success) {
        setSelectedTicket({
          ...selectedTicket,
          status
        });
        setTickets(tickets.map(t => t.id === selectedTicket.id ? {
          ...t,
          status
        } : t));
        showToast('success', isRTL ? 'تم تحديث الحالة' : 'Status updated');
      }
    } catch (error) {
      console.error('Status update error:', error);
    }
  };
  const loadUserDetails = async (user: SupabaseUser) => {
    setSelectedUser(user);
    setIsLoadingUserDetails(true);
    try {
      const [products, offers, customers, tickets, activities] = await Promise.all([dbProducts.getAll(user.email), dbOffers.getAll(user.email), dbCustomers.getAll(user.email, user.instance_name), dbSupportTickets.getAll(user.email), dbActivityLog.getByUser(user.email)]);
      setUserProducts(products);
      setUserOffers(offers);
      setUserCustomers(customers);
      setUserTickets(tickets);
      setUserActivities(activities);
    } catch (error) {
      console.error('Failed to load user details:', error);
      showToast('error', isRTL ? 'فشل تحميل بيانات المستخدم' : 'Failed to load user details');
    } finally {
      setIsLoadingUserDetails(false);
    }
  };
  const handleToggleUserSuspend = async (user: SupabaseUser) => {
    const willSuspend = !user.is_account_suspended;
    if (willSuspend && !confirm(isRTL ? 'هل أنت متأكد من تعليق هذا الحساب؟' : 'Are you sure you want to suspend this account?')) {
      return;
    }
    setIsTogglingUserSuspend(true);
    try {
      const success = await dbUsers.update(user.email, {
        is_account_suspended: willSuspend
      });
      if (success) {
        const updatedUser = {
          ...user,
          is_account_suspended: willSuspend
        };
        setSelectedUser(updatedUser);
        setUsers(users.map(u => u.email === user.email ? updatedUser : u));
        // Log activity
        if (willSuspend) {
          await logActivity.accountSuspended(user.email, adminUser?.name || 'Admin');
        } else {
          await logActivity.accountActivated(user.email, adminUser?.name || 'Admin');
        }
        // Refresh activities
        const activities = await dbActivityLog.getByUser(user.email);
        setUserActivities(activities);
        showToast('success', isRTL ? willSuspend ? 'تم تعليق الحساب' : 'تم تفعيل الحساب' : willSuspend ? 'Account suspended' : 'Account activated');
      } else {
        showToast('error', isRTL ? 'فشل التحديث' : 'Update failed');
      }
    } catch (error) {
      console.error('Toggle suspend error:', error);
      showToast('error', isRTL ? 'حدث خطأ' : 'An error occurred');
    } finally {
      setIsTogglingUserSuspend(false);
    }
  };
  const handleToggleUserBot = async (user: SupabaseUser, enable: boolean) => {
    if (!user.instance_name) {
      showToast('error', isRTL ? 'لا يوجد اسم للنسخة' : 'No instance name found');
      return;
    }
    setIsTogglingUserBot(true);
    try {
      const url = enable ? 'https://n8n.operines.me/webhook/seup-whatsapp-operines1' : 'https://n8n.operines.me/webhook/disable';
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          instance_name: user.instance_name
        })
      });
      if (response.ok) {
        // Log activity
        await logActivity.botToggle(user.email, enable, adminUser?.name || 'Admin');
        // Refresh activities
        const activities = await dbActivityLog.getByUser(user.email);
        setUserActivities(activities);
        showToast('success', isRTL ? enable ? 'تم تفعيل البوت' : 'تم تعطيل البوت' : enable ? 'Bot enabled' : 'Bot disabled');
      } else {
        throw new Error('Webhook failed');
      }
    } catch (error) {
      console.error('Toggle bot error:', error);
      showToast('error', isRTL ? 'فشل تحديث البوت' : 'Failed to update bot');
    } finally {
      setIsTogglingUserBot(false);
    }
  };
  const handleChangeUserPlan = async (user: SupabaseUser, newPlan: string) => {
    try {
      const oldPlan = user.plan;
      const success = await dbUsers.update(user.email, {
        plan: newPlan
      });
      if (success) {
        const updatedUser = {
          ...user,
          plan: newPlan
        };
        setSelectedUser(updatedUser);
        setUsers(users.map(u => u.email === user.email ? updatedUser : u));
        // Log activity
        await logActivity.planChange(user.email, oldPlan, newPlan, adminUser?.name || 'Admin');
        // Refresh activities
        const activities = await dbActivityLog.getByUser(user.email);
        setUserActivities(activities);
        showToast('success', isRTL ? 'تم تحديث الخطة' : 'Plan updated successfully');
      } else {
        showToast('error', isRTL ? 'فشل التحديث' : 'Update failed');
      }
    } catch (error) {
      console.error('Change plan error:', error);
      showToast('error', isRTL ? 'حدث خطأ' : 'An error occurred');
    }
  };
  const handleDeleteUser = async (user: SupabaseUser) => {
    if (!confirm(isRTL ? 'هل أنت متأكد من حذف هذا المستخدم نهائياً؟ لا يمكن التراجع عن هذا الإجراء.' : 'Are you sure you want to permanently delete this user? This cannot be undone.')) {
      return;
    }
    setIsDeletingUser(true);
    try {
      const success = await dbUsers.delete(user.email);
      if (success) {
        setUsers(users.filter(u => u.email !== user.email));
        setSelectedUser(null);
        // Log activity
        await logActivity.adminAction(user.email, 'User deleted', adminUser?.name || 'Admin');
        showToast('success', isRTL ? 'تم حذف المستخدم بنجاح' : 'User deleted successfully');
      } else {
        showToast('error', isRTL ? 'فشل حذف المستخدم' : 'Failed to delete user');
      }
    } catch (error) {
      console.error('Delete user error:', error);
      showToast('error', isRTL ? 'حدث خطأ' : 'An error occurred');
    } finally {
      setIsDeletingUser(false);
    }
  };
  const handleExportUsers = () => {
    try {
      const headers = ['Name', 'Email', 'Phone', 'Plan', 'Status', 'Brand Name', 'Country', 'Instance Name', 'Joined Date'];
      const csvContent = [headers.join(','), ...users.map(u => [`"${u.name || ''}"`, `"${u.email || ''}"`, `"${u.phone || ''}"`, `"${u.plan || ''}"`, `"${u.is_account_suspended ? 'Suspended' : 'Active'}"`, `"${u.brand_name || ''}"`, `"${u.country || ''}"`, `"${u.instance_name || ''}"`, `"${u.created_at ? new Date(u.created_at).toLocaleDateString() : ''}"`].join(','))].join('\n');
      const blob = new Blob([csvContent], {
        type: 'text/csv;charset=utf-8;'
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', `operines_users_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Export users error:', error);
      showToast('error', isRTL ? 'فشل تصدير المستخدمين' : 'Failed to export users');
    }
  };
  const handleImportUsers = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setIsImportingUsers(true);
    try {
      const text = await file.text();
      const rows = text.split('\n').map(row => row.split(','));
      const headers = rows[0].map(h => h.trim().toLowerCase().replace(/"/g, ''));
      const emailIdx = headers.findIndex(h => h.includes('email'));
      const nameIdx = headers.findIndex(h => h.includes('name'));
      const phoneIdx = headers.findIndex(h => h.includes('phone'));
      const planIdx = headers.findIndex(h => h.includes('plan'));
      const countryIdx = headers.findIndex(h => h.includes('country'));
      const brandIdx = headers.findIndex(h => h.includes('brand'));
      if (emailIdx === -1 || nameIdx === -1) {
        showToast('error', isRTL ? 'ملف CSV غير صالح. يجب أن يحتوي على أعمدة Name و Email' : 'Invalid CSV. Must contain Name and Email columns');
        setIsImportingUsers(false);
        return;
      }
      let successCount = 0;
      let errorCount = 0;
      for (let i = 1; i < rows.length; i++) {
        const row = rows[i];
        if (row.length < 2 || !row[emailIdx]) continue;
        const email = row[emailIdx].replace(/"/g, '').trim();
        const name = row[nameIdx].replace(/"/g, '').trim();
        const phone = phoneIdx !== -1 ? row[phoneIdx].replace(/"/g, '').trim() : '';
        const plan = planIdx !== -1 ? row[planIdx].replace(/"/g, '').trim() : 'trial';
        const country = countryIdx !== -1 ? row[countryIdx].replace(/"/g, '').trim() : '';
        const brand_name = brandIdx !== -1 ? row[brandIdx].replace(/"/g, '').trim() : '';
        if (!email || !name) continue;
        try {
          const existingUser = await dbUsers.getByEmail(email);
          if (!existingUser) {
            const {
              hashPassword
            } = await import('../utils/security');
            const defaultPassword = await hashPassword('Operines@123');
            await dbUsers.create({
              email,
              name,
              phone,
              plan,
              country,
              brand_name,
              password: defaultPassword
            });
            successCount++;
          } else {
            errorCount++;
          }
        } catch (err) {
          errorCount++;
        }
      }
      showToast('success', isRTL ? `تم استيراد ${successCount} مستخدم بنجاح. ${errorCount} فشلوا أو موجودين مسبقاً.` : `Imported ${successCount} users. ${errorCount} failed or already exist.`);
      loadDashboardData(); // Refresh list
    } catch (error) {
      console.error('Import users error:', error);
      showToast('error', isRTL ? 'فشل استيراد المستخدمين' : 'Failed to import users');
    } finally {
      setIsImportingUsers(false);
      // Reset file input
      e.target.value = '';
    }
  };
  const handleAdminCreateTicket = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUser || !createTicketSubject.trim() || !createTicketMessage.trim() || !adminUser) return;
    setIsCreatingTicket(true);
    try {
      const ticket = await dbSupportTickets.create({
        user_email: selectedUser.email,
        subject: `[Admin] ${createTicketSubject}`,
        message: createTicketMessage,
        priority: createTicketPriority,
        status: 'open'
      });
      if (ticket) {
        // Add admin's first reply automatically
        await dbTicketReplies.create({
          ticket_id: ticket.id!,
          from_type: 'admin',
          from_name: adminUser.name,
          message: createTicketMessage
        });
        // Notify the user
        await dbUserNotifications.create({
          user_email: selectedUser.email,
          title: 'New support ticket from admin',
          title_ar: 'تذكرة دعم جديدة من الإدارة',
          message: `Admin has opened a support ticket: ${createTicketSubject}`,
          message_ar: `قام فريق الدعم بفتح تذكرة: ${createTicketSubject}`,
          type: 'info',
          read: false,
          sent_by: adminUser.name
        });
        // Log activity
        await logActivity.ticketCreated(selectedUser.email, createTicketSubject, adminUser.name);
        // Refresh activities
        const activities = await dbActivityLog.getByUser(selectedUser.email);
        setUserActivities(activities);
        // Update local state
        setUserTickets([ticket, ...userTickets]);
        setTickets([ticket, ...tickets]);
        // Reset form
        setIsCreateTicketOpen(false);
        setCreateTicketSubject('');
        setCreateTicketMessage('');
        setCreateTicketPriority('medium');
        showToast('success', isRTL ? 'تم إنشاء التذكرة بنجاح' : 'Ticket created successfully');
      } else {
        showToast('error', isRTL ? 'فشل إنشاء التذكرة' : 'Failed to create ticket');
      }
    } catch (error) {
      console.error('Create ticket error:', error);
      showToast('error', isRTL ? 'حدث خطأ' : 'An error occurred');
    } finally {
      setIsCreatingTicket(false);
    }
  };
  const handleSendNotification = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!notifEmail || !notifTitleEn || !notifTitleAr || !notifMessageEn || !notifMessageAr || !adminUser) {
      showToast('error', isRTL ? 'يرجى ملء جميع الحقول' : 'Please fill all fields');
      return;
    }
    setIsSendingNotif(true);
    try {
      if (notifEmail === 'all') {
        // Send to all users
        const promises = users.map(u => dbUserNotifications.create({
          user_email: u.email,
          title: notifTitleEn,
          title_ar: notifTitleAr,
          message: notifMessageEn,
          message_ar: notifMessageAr,
          type: notifType,
          read: false,
          sent_by: adminUser.name
        }));
        await Promise.allSettled(promises);
        showToast('success', isRTL ? `تم إرسال الإشعار لـ ${users.length} مستخدم` : `Sent to ${users.length} users`);
      } else {
        // Send to specific user
        const success = await dbUserNotifications.create({
          user_email: notifEmail,
          title: notifTitleEn,
          title_ar: notifTitleAr,
          message: notifMessageEn,
          message_ar: notifMessageAr,
          type: notifType,
          read: false,
          sent_by: adminUser.name
        });
        if (success) {
          showToast('success', isRTL ? 'تم إرسال الإشعار بنجاح' : 'Notification sent successfully');
        } else {
          throw new Error('Failed to create notification');
        }
      }
      // Reset form
      setNotifEmail('');
      setNotifTitleEn('');
      setNotifTitleAr('');
      setNotifMessageEn('');
      setNotifMessageAr('');
      setNotifType('info');
    } catch (error) {
      console.error('Send notification error:', error);
      showToast('error', isRTL ? 'فشل إرسال الإشعار' : 'Failed to send notification');
    } finally {
      setIsSendingNotif(false);
    }
  };
  // --- Render Helpers ---
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'open':
        return <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800" data-id="element-878">
            <AlertCircle className="w-3 h-3" data-id="element-879" />
            {isRTL ? 'مفتوحة' : 'Open'}
          </span>;
      case 'in_progress':
        return <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800" data-id="element-880">
            <Clock className="w-3 h-3" data-id="element-881" />
            {isRTL ? 'قيد المعالجة' : 'In Progress'}
          </span>;
      case 'resolved':
      case 'closed':
        return <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-882">
            <CheckCircle2 className="w-3 h-3" data-id="element-883" />
            {isRTL ? 'مغلقة' : 'Closed'}
          </span>;
      default:
        return null;
    }
  };
  const getPlanBadge = (plan: string) => {
    switch (plan?.toLowerCase()) {
      case 'trial':
        return <span className="px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800" data-id="element-884">
            Trial
          </span>;
      case 'starter':
        return <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800" data-id="element-885">
            Starter
          </span>;
      case 'business':
        return <span className="px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800" data-id="element-886">
            Business
          </span>;
      case 'pro':
        return <span className="px-2 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800" data-id="element-887">
            Pro
          </span>;
      default:
        return <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-888">
            {plan || 'None'}
          </span>;
    }
  };
  // --- Views ---
  if (isCheckingAuth) {
    return <div className="min-h-screen flex items-center justify-center bg-gray-50" data-id="element-889">
        <div className="animate-spin w-8 h-8 border-4 border-[#4f1964] border-t-transparent rounded-full" data-id="element-890" />
      </div>;
  }
  if (!adminUser) {
    return <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4" dir={isRTL ? 'rtl' : 'ltr'} data-id="element-891">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="bg-white p-8 rounded-2xl shadow-xl w-full max-w-md border border-gray-100" data-id="element-892">
          <div className="text-center mb-8" data-id="element-893">
            <div className="w-16 h-16 bg-[#4f1964] rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg" data-id="element-894">
              <Shield className="w-8 h-8 text-white" data-id="element-895" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900" data-id="element-896">
              {isRTL ? 'لوحة تحكم الإدارة' : 'Admin Dashboard'}
            </h1>
            <p className="text-gray-500 mt-2" data-id="element-897">
              {isRTL ? 'تسجيل الدخول للموظفين فقط' : 'Staff login only'}
            </p>
          </div>

          <form onSubmit={handleLogin} className="space-y-5" data-id="element-898">
            <div data-id="element-899">
              <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-900">
                {isRTL ? 'البريد الإلكتروني' : 'Email Address'}
              </label>
              <div className="relative" data-id="element-901">
                <Mail className="absolute top-3 left-3 w-5 h-5 text-gray-400" data-id="element-902" />
                <input type="email" value={loginEmail} onChange={e => setLoginEmail(e.target.value)} className="w-full pl-10 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4f1964] outline-none" required dir="ltr" data-id="element-903" />
              </div>
            </div>
            <div data-id="element-904">
              <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-905">
                {isRTL ? 'كلمة المرور' : 'Password'}
              </label>
              <div className="relative" data-id="element-906">
                <Lock className="absolute top-3 left-3 w-5 h-5 text-gray-400" data-id="element-907" />
                <input type="password" value={loginPassword} onChange={e => setLoginPassword(e.target.value)} className="w-full pl-10 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4f1964] outline-none" required dir="ltr" data-id="element-908" />
              </div>
            </div>
            <Button type="submit" className="w-full bg-[#4f1964] text-white hover:bg-[#3d1350] py-3" disabled={isLoggingIn} data-id="element-909">
              {isLoggingIn ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mx-auto" data-id="element-910" /> : isRTL ? 'تسجيل الدخول' : 'Login'}
            </Button>
          </form>
        </motion.div>
      </div>;
  }
  const sidebarItems = [{
    id: 'overview',
    label: isRTL ? 'نظرة عامة' : 'Overview',
    icon: LayoutDashboard
  }, {
    id: 'users',
    label: isRTL ? 'المستخدمين' : 'Users',
    icon: Users
  }, {
    id: 'tickets',
    label: isRTL ? 'تذاكر الدعم' : 'Support Tickets',
    icon: MessageSquare
  }, {
    id: 'staff',
    label: isRTL ? 'الموظفين' : 'Staff',
    icon: Shield
  }, {
    id: 'notifications',
    label: isRTL ? 'الإشعارات' : 'Notifications',
    icon: Bell
  }];
  // --- Render Sections ---
  const renderOverview = () => {
    const activeUsers = users.filter(u => !u.is_account_suspended).length;
    const openTickets = tickets.filter(t => t.status === 'open').length;
    const trialUsers = users.filter(u => u.plan === 'trial').length;
    const paidUsers = users.filter(u => u.plan !== 'trial').length;
    return <div className="space-y-6" data-id="element-911">
        <h2 className="text-2xl font-bold text-gray-900" data-id="element-912">
          {isRTL ? 'نظرة عامة' : 'Overview'}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-913">
          {[{
          label: isRTL ? 'إجمالي المستخدمين' : 'Total Users',
          value: users.length,
          icon: Users,
          color: 'text-blue-600',
          bg: 'bg-blue-50'
        }, {
          label: isRTL ? 'تذاكر مفتوحة' : 'Open Tickets',
          value: openTickets,
          icon: AlertCircle,
          color: 'text-red-600',
          bg: 'bg-red-50'
        }, {
          label: isRTL ? 'مشتركين (مدفوع)' : 'Paid Subscribers',
          value: paidUsers,
          icon: CreditCard,
          color: 'text-green-600',
          bg: 'bg-green-50'
        }, {
          label: isRTL ? 'فترة تجريبية' : 'Trial Users',
          value: trialUsers,
          icon: Clock,
          color: 'text-yellow-600',
          bg: 'bg-yellow-50'
        }].map((stat, i) => <div key={i} className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm" data-id="element-914">
              <div className="flex justify-between items-start mb-4" data-id="element-915">
                <div className={`p-3 rounded-lg ${stat.bg}`} data-id="element-916">
                  <stat.icon className={`w-6 h-6 ${stat.color}`} data-id="element-917" />
                </div>
                <span className="text-2xl font-bold text-gray-900" data-id="element-918">
                  {stat.value}
                </span>
              </div>
              <h3 className="text-gray-600 font-medium" data-id="element-919">{stat.label}</h3>
            </div>)}
        </div>
      </div>;
  };
  const renderUsers = () => {
    const filteredUsers = users.filter(u => u.email.toLowerCase().includes(userSearch.toLowerCase()) || u.name.toLowerCase().includes(userSearch.toLowerCase()) || u.phone && u.phone.includes(userSearch));
    // User Detail View
    if (selectedUser) {
      return <div className="space-y-6 max-w-6xl mx-auto" data-id="element-920">
          <button onClick={() => {
          setSelectedUser(null);
          setUserProducts([]);
          setUserOffers([]);
          setUserCustomers([]);
          setUserTickets([]);
        }} className="text-gray-500 hover:text-[#4f1964] flex items-center gap-2 text-sm font-medium" data-id="element-921">
            <ArrowLeft className="w-4 h-4" data-id="element-922" />
            {isRTL ? 'العودة للقائمة' : 'Back to list'}
          </button>

          {isLoadingUserDetails ? <div className="flex items-center justify-center py-12" data-id="element-923">
              <div className="w-8 h-8 border-4 border-[#4f1964] border-t-transparent rounded-full animate-spin" data-id="element-924" />
            </div> : <>
              {/* User Info Card */}
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6" data-id="element-925">
                <div className="flex flex-col md:flex-row gap-6" data-id="element-926">
                  <div className="flex items-start gap-4 flex-1" data-id="element-927">
                    <div className="w-16 h-16 rounded-full bg-[#4f1964] flex items-center justify-center text-white text-2xl font-bold flex-shrink-0" data-id="element-928">
                      {selectedUser.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-929">
                      <h3 className="text-xl font-bold text-gray-900 mb-1" data-id="element-930">
                        {selectedUser.name}
                      </h3>
                      {selectedUser.brand_name && <p className="text-sm text-gray-600 mb-2" data-id="element-931">
                          {selectedUser.brand_name}
                        </p>}
                      <div className="space-y-1.5 text-sm text-gray-600" data-id="element-932">
                        <div className="flex items-center gap-2" data-id="element-933">
                          <Mail className="w-4 h-4 text-gray-400" data-id="element-934" />
                          <span dir="ltr" data-id="element-935">{selectedUser.email}</span>
                        </div>
                        {selectedUser.phone && <div className="flex items-center gap-2" data-id="element-936">
                            <User className="w-4 h-4 text-gray-400" data-id="element-937" />
                            <span dir="ltr" data-id="element-938">{selectedUser.phone}</span>
                          </div>}
                        {selectedUser.instance_name && <div className="flex items-center gap-2" data-id="element-939">
                            <Bot className="w-4 h-4 text-gray-400" data-id="element-940" />
                            <span data-id="element-941">{selectedUser.instance_name}</span>
                          </div>}
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col gap-3 md:items-end" data-id="element-942">
                    <div className="flex flex-wrap gap-2" data-id="element-943">
                      {getPlanBadge(selectedUser.plan)}
                      {selectedUser.is_account_suspended ? <span className="px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800" data-id="element-944">
                          Suspended
                        </span> : <span className="px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-945">
                          Active
                        </span>}
                    </div>
                    <p className="text-xs text-gray-500" data-id="element-946">
                      {isRTL ? 'انضم في:' : 'Joined:'}{' '}
                      {new Date(selectedUser.created_at!).toLocaleDateString()}
                    </p>
                    {selectedUser.trial_start_date && <p className="text-xs text-gray-500" data-id="element-947">
                        {isRTL ? 'الفترة التجريبية:' : 'Trial:'}{' '}
                        {new Date(selectedUser.trial_start_date).toLocaleDateString()}{' '}
                        -{' '}
                        {new Date(selectedUser.trial_end_date!).toLocaleDateString()}
                      </p>}
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6" data-id="element-948">
                <h4 className="font-bold text-gray-900 mb-4 flex items-center gap-2" data-id="element-949">
                  <Settings className="w-5 h-5" data-id="element-950" />
                  {isRTL ? 'إجراءات الحساب' : 'Account Actions'}
                </h4>
                <div className="flex flex-wrap gap-3" data-id="element-951">
                  <Button onClick={() => handleToggleUserSuspend(selectedUser)} disabled={isTogglingUserSuspend} className={`flex items-center gap-2 ${selectedUser.is_account_suspended ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'} text-white`} data-id="element-952">
                    {isTogglingUserSuspend ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-953" /> : selectedUser.is_account_suspended ? <Power className="w-4 h-4" data-id="element-954" /> : <Ban className="w-4 h-4" data-id="element-955" />}
                    {selectedUser.is_account_suspended ? isRTL ? 'تفعيل الحساب' : 'Activate Account' : isRTL ? 'تعليق الحساب' : 'Suspend Account'}
                  </Button>

                  {selectedUser.instance_name && <>
                      <Button onClick={() => handleToggleUserBot(selectedUser, true)} disabled={isTogglingUserBot} className="flex items-center gap-2 bg-[#4f1964] hover:bg-[#3d1350] text-white" data-id="element-956">
                        {isTogglingUserBot ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-957" /> : <Power className="w-4 h-4" data-id="element-958" />}
                        {isRTL ? 'تفعيل البوت' : 'Enable Bot'}
                      </Button>
                      <Button onClick={() => handleToggleUserBot(selectedUser, false)} disabled={isTogglingUserBot} variant="outline" className="flex items-center gap-2" data-id="element-959">
                        {isTogglingUserBot ? <div className="w-4 h-4 border-2 border-gray-400/30 border-t-gray-400 rounded-full animate-spin" data-id="element-960" /> : <Ban className="w-4 h-4" data-id="element-961" />}
                        {isRTL ? 'تعطيل البوت' : 'Disable Bot'}
                      </Button>
                    </>}

                  <div className="flex items-center gap-2" data-id="element-962">
                    <label className="text-sm font-medium text-gray-700" data-id="element-963">
                      {isRTL ? 'تغيير الخطة:' : 'Change Plan:'}
                    </label>
                    <select value={selectedUser.plan} onChange={e => handleChangeUserPlan(selectedUser, e.target.value)} className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#4f1964] outline-none" data-id="element-964">
                      <option value="trial" data-id="element-965">Trial</option>
                      <option value="starter" data-id="element-966">Starter</option>
                      <option value="business" data-id="element-967">Business</option>
                      <option value="pro" data-id="element-968">Pro</option>
                    </select>
                  </div>

                  <Button onClick={() => handleDeleteUser(selectedUser)} disabled={isDeletingUser} className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white ml-auto" data-id="element-969">
                    {isDeletingUser ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-970" /> : <Trash2 className="w-4 h-4" data-id="element-971" />}
                    {isRTL ? 'حذف المستخدم' : 'Delete User'}
                  </Button>
                </div>
              </div>

              {/* Data Sections Grid */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-972">
                {/* Products Section */}
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6" data-id="element-973">
                  <h4 className="font-bold text-gray-900 mb-4 flex items-center gap-2" data-id="element-974">
                    <Package className="w-5 h-5 text-[#4f1964]" data-id="element-975" />
                    {isRTL ? 'المنتجات' : 'Products'}
                    <span className="ml-auto text-sm font-normal text-gray-500" data-id="element-976">
                      ({userProducts.length})
                    </span>
                  </h4>
                  {userProducts.length === 0 ? <p className="text-sm text-gray-500 text-center py-8" data-id="element-977">
                      {isRTL ? 'لا توجد منتجات' : 'No products'}
                    </p> : <div className="space-y-3 max-h-64 overflow-y-auto" data-id="element-978">
                      {userProducts.map(p => <div key={p.id} className="p-3 border border-gray-200 rounded-lg hover:bg-gray-50" data-id="element-979">
                          <div className="flex justify-between items-start mb-1" data-id="element-980">
                            <h5 className="font-medium text-gray-900 text-sm" data-id="element-981">
                              {p.name}
                            </h5>
                            <span className="text-sm font-bold text-[#4f1964]" data-id="element-982">
                              {p.price}
                            </span>
                          </div>
                          <p className="text-xs text-gray-500 mb-1" data-id="element-983">{p.type}</p>
                          {p.description && <p className="text-xs text-gray-600 line-clamp-2" data-id="element-984">
                              {p.description}
                            </p>}
                        </div>)}
                    </div>}
                </div>

                {/* Offers Section */}
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6" data-id="element-985">
                  <h4 className="font-bold text-gray-900 mb-4 flex items-center gap-2" data-id="element-986">
                    <Tag className="w-5 h-5 text-[#4f1964]" data-id="element-987" />
                    {isRTL ? 'العروض' : 'Offers'}
                    <span className="ml-auto text-sm font-normal text-gray-500" data-id="element-988">
                      ({userOffers.length})
                    </span>
                  </h4>
                  {userOffers.length === 0 ? <p className="text-sm text-gray-500 text-center py-8" data-id="element-989">
                      {isRTL ? 'لا توجد عروض' : 'No offers'}
                    </p> : <div className="space-y-3 max-h-64 overflow-y-auto" data-id="element-990">
                      {userOffers.map(o => <div key={o.id} className="p-3 border border-gray-200 rounded-lg hover:bg-gray-50" data-id="element-991">
                          <div className="flex justify-between items-start mb-1" data-id="element-992">
                            <h5 className="font-medium text-gray-900 text-sm" data-id="element-993">
                              {o.title}
                            </h5>
                            <span className="text-sm font-bold text-green-600" data-id="element-994">
                              {o.discount}
                            </span>
                          </div>
                          {(o.start_date || o.end_date) && <p className="text-xs text-gray-500 mb-1" data-id="element-995">
                              {o.start_date} - {o.end_date}
                            </p>}
                          {o.active !== undefined && <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${o.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`} data-id="element-996">
                              {o.active ? isRTL ? 'نشط' : 'Active' : isRTL ? 'غير نشط' : 'Inactive'}
                            </span>}
                        </div>)}
                    </div>}
                </div>

                {/* Customers Section */}
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6" data-id="element-997">
                  <h4 className="font-bold text-gray-900 mb-4 flex items-center gap-2" data-id="element-998">
                    <UserCheck className="w-5 h-5 text-[#4f1964]" data-id="element-999" />
                    {isRTL ? 'العملاء' : 'Customers'}
                    <span className="ml-auto text-sm font-normal text-gray-500" data-id="element-1000">
                      ({userCustomers.length})
                    </span>
                  </h4>
                  {userCustomers.length === 0 ? <p className="text-sm text-gray-500 text-center py-8" data-id="element-1001">
                      {isRTL ? 'لا يوجد عملاء' : 'No customers'}
                    </p> : <div className="space-y-3 max-h-64 overflow-y-auto" data-id="element-1002">
                      {userCustomers.map(c => <div key={c.id} className="p-3 border border-gray-200 rounded-lg hover:bg-gray-50" data-id="element-1003">
                          <h5 className="font-medium text-gray-900 text-sm mb-1" data-id="element-1004">
                            {c.name}
                          </h5>
                          {c.phone && <p className="text-xs text-gray-600" dir="ltr" data-id="element-1005">
                              {c.phone}
                            </p>}
                          {c.note && <p className="text-xs text-gray-500 mt-1 line-clamp-2" data-id="element-1006">
                              {c.note}
                            </p>}
                        </div>)}
                    </div>}
                </div>

                {/* Tickets Section */}
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6" data-id="element-1007">
                  <h4 className="font-bold text-gray-900 mb-4 flex items-center gap-2" data-id="element-1008">
                    <MessageSquare className="w-5 h-5 text-[#4f1964]" data-id="element-1009" />
                    {isRTL ? 'تذاكر الدعم' : 'Support Tickets'}
                    <span className="ml-auto text-sm font-normal text-gray-500" data-id="element-1010">
                      ({userTickets.length})
                    </span>
                    <button onClick={() => setIsCreateTicketOpen(true)} className="ml-2 flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium bg-[#4f1964] text-white hover:bg-[#3d1350] transition-colors" data-id="element-1011">
                      <Plus className="w-3.5 h-3.5" data-id="element-1012" />
                      {isRTL ? 'تذكرة جديدة' : 'New Ticket'}
                    </button>
                  </h4>
                  {userTickets.length === 0 ? <p className="text-sm text-gray-500 text-center py-8" data-id="element-1013">
                      {isRTL ? 'لا توجد تذاكر' : 'No tickets'}
                    </p> : <div className="space-y-3 max-h-64 overflow-y-auto" data-id="element-1014">
                      {userTickets.map(t => <div key={t.id} className="p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer" onClick={() => {
                  setActiveSection('tickets');
                  handleTicketClick(t);
                }} data-id="element-1015">
                          <div className="flex justify-between items-start mb-1" data-id="element-1016">
                            <h5 className="font-medium text-gray-900 text-sm" data-id="element-1017">
                              {t.subject}
                            </h5>
                            {getStatusBadge(t.status)}
                          </div>
                          <p className="text-xs text-gray-500" data-id="element-1018">
                            {new Date(t.created_at!).toLocaleDateString()}
                          </p>
                        </div>)}
                    </div>}
                </div>
              </div>

              {/* Activity Log Section */}
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 mt-6" data-id="element-1019">
                <h4 className="font-bold text-gray-900 mb-6 flex items-center gap-2" data-id="element-1020">
                  <Activity className="w-5 h-5 text-[#4f1964]" data-id="element-1021" />
                  {isRTL ? 'سجل النشاطات' : 'Activity Log'}
                  <span className="ml-auto text-sm font-normal text-gray-500" data-id="element-1022">
                    ({userActivities.length})
                  </span>
                </h4>

                {userActivities.length === 0 ? <div className="text-center py-12 text-gray-500" data-id="element-1023">
                    <Activity className="w-12 h-12 text-gray-300 mx-auto mb-3" data-id="element-1024" />
                    <p data-id="element-1025">
                      {isRTL ? 'لا توجد نشاطات مسجلة' : 'No activities recorded'}
                    </p>
                  </div> : <div className="relative border-l-2 border-gray-100 ml-3 md:ml-4 space-y-6 max-h-96 overflow-y-auto pr-4" data-id="element-1026">
                    {userActivities.map(activity => <div key={activity.id} className="relative pl-6 md:pl-8" data-id="element-1027">
                        {/* Timeline dot */}
                        <div className={`absolute -left-[9px] top-1 w-4 h-4 rounded-full border-2 border-white ${activity.activity_type.includes('payment_success') ? 'bg-green-500' : activity.activity_type.includes('payment_failed') ? 'bg-red-500' : activity.activity_type.includes('login') ? 'bg-blue-500' : activity.activity_type.includes('register') ? 'bg-purple-500' : activity.activity_type.includes('plan') ? 'bg-indigo-500' : 'bg-gray-400'}`} data-id="element-1028" />

                        <div className="bg-gray-50 rounded-lg p-4 border border-gray-100" data-id="element-1029">
                          <div className="flex justify-between items-start mb-1" data-id="element-1030">
                            <h5 className="font-medium text-gray-900 text-sm" data-id="element-1031">
                              {isRTL ? activity.description_ar : activity.description}
                            </h5>
                            <span className="text-xs text-gray-500 whitespace-nowrap ml-4" data-id="element-1032">
                              {new Date(activity.created_at!).toLocaleString()}
                            </span>
                          </div>

                          <div className="flex items-center gap-4 mt-2 text-xs text-gray-500" data-id="element-1033">
                            <span className="capitalize bg-white px-2 py-1 rounded border border-gray-200" data-id="element-1034">
                              {activity.activity_type.replace(/_/g, ' ')}
                            </span>
                            {activity.performed_by && <span className="flex items-center gap-1" data-id="element-1035">
                                <User className="w-3 h-3" data-id="element-1036" />
                                {activity.performed_by}
                              </span>}
                          </div>

                          {/* Show metadata if present and not empty */}
                          {activity.metadata && Object.keys(activity.metadata).length > 0 && <div className="mt-3 bg-white p-2 rounded border border-gray-200 text-xs font-mono text-gray-600 overflow-x-auto" data-id="element-1037">
                                {JSON.stringify(activity.metadata, null, 2)}
                              </div>}
                        </div>
                      </div>)}
                  </div>}
              </div>

              {/* Admin Create Ticket Modal */}
              <AnimatePresence data-id="element-1038">
                {isCreateTicketOpen && <motion.div initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} exit={{
              opacity: 0
            }} className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => !isCreatingTicket && setIsCreateTicketOpen(false)} data-id="element-1039">
                    <motion.div initial={{
                scale: 0.95,
                opacity: 0
              }} animate={{
                scale: 1,
                opacity: 1
              }} exit={{
                scale: 0.95,
                opacity: 0
              }} onClick={e => e.stopPropagation()} className="bg-white rounded-2xl shadow-xl w-full max-w-lg overflow-hidden" data-id="element-1040">
                      <div className="p-6 border-b border-gray-100 flex justify-between items-center" data-id="element-1041">
                        <div data-id="element-1042">
                          <h3 className="text-lg font-bold text-gray-900" data-id="element-1043">
                            {isRTL ? 'إنشاء تذكرة دعم' : 'Create Support Ticket'}
                          </h3>
                          <p className="text-sm text-gray-500 mt-0.5" data-id="element-1044">
                            {isRTL ? 'لـ' : 'For'} {selectedUser?.name} (
                            {selectedUser?.email})
                          </p>
                        </div>
                        <button onClick={() => !isCreatingTicket && setIsCreateTicketOpen(false)} className="text-gray-400 hover:text-gray-600" disabled={isCreatingTicket} data-id="element-1045">
                          <X className="w-5 h-5" data-id="element-1046" />
                        </button>
                      </div>
                      <form onSubmit={handleAdminCreateTicket} className="p-6 space-y-5" data-id="element-1047">
                        <div data-id="element-1048">
                          <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-1049">
                            {isRTL ? 'الموضوع' : 'Subject'}
                          </label>
                          <input type="text" value={createTicketSubject} onChange={e => setCreateTicketSubject(e.target.value)} placeholder={isRTL ? 'موضوع التذكرة...' : 'Ticket subject...'} className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4f1964] outline-none" required disabled={isCreatingTicket} data-id="element-1050" />
                        </div>
                        <div data-id="element-1051">
                          <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-1052">
                            {isRTL ? 'الأولوية' : 'Priority'}
                          </label>
                          <div className="grid grid-cols-3 gap-3" data-id="element-1053">
                            {[{
                        id: 'low' as const,
                        label: isRTL ? 'منخفضة' : 'Low',
                        color: 'green'
                      }, {
                        id: 'medium' as const,
                        label: isRTL ? 'متوسطة' : 'Medium',
                        color: 'yellow'
                      }, {
                        id: 'high' as const,
                        label: isRTL ? 'عالية' : 'High',
                        color: 'red'
                      }].map(p => <button key={p.id} type="button" onClick={() => setCreateTicketPriority(p.id)} className={`py-2 px-3 rounded-lg text-sm font-medium border transition-all ${createTicketPriority === p.id ? p.color === 'green' ? 'bg-green-50 border-green-200 text-green-700' : p.color === 'yellow' ? 'bg-yellow-50 border-yellow-200 text-yellow-700' : 'bg-red-50 border-red-200 text-red-700' : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'}`} data-id="element-1054">
                                {p.label}
                              </button>)}
                          </div>
                        </div>
                        <div data-id="element-1055">
                          <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-1056">
                            {isRTL ? 'الرسالة' : 'Message'}
                          </label>
                          <textarea value={createTicketMessage} onChange={e => setCreateTicketMessage(e.target.value)} placeholder={isRTL ? 'اكتب رسالتك للمستخدم...' : 'Write your message to the user...'} className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4f1964] outline-none resize-none" rows={5} required disabled={isCreatingTicket} data-id="element-1057" />
                        </div>
                        <div className="pt-2 flex gap-3" data-id="element-1058">
                          <Button type="button" variant="outline" onClick={() => setIsCreateTicketOpen(false)} className="flex-1" disabled={isCreatingTicket} data-id="element-1059">
                            {isRTL ? 'إلغاء' : 'Cancel'}
                          </Button>
                          <Button type="submit" className="flex-1 bg-[#4f1964] text-white hover:bg-[#3d1350] flex items-center justify-center gap-2" disabled={isCreatingTicket || !createTicketSubject.trim() || !createTicketMessage.trim()} data-id="element-1060">
                            {isCreatingTicket ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-1061" /> : <>
                                <Send className="w-4 h-4" data-id="element-1062" />
                                {isRTL ? 'إنشاء التذكرة' : 'Create Ticket'}
                              </>}
                          </Button>
                        </div>
                      </form>
                    </motion.div>
                  </motion.div>}
              </AnimatePresence>
            </>}
        </div>;
    }
    // Users Table View
    return <div className="space-y-6" data-id="element-1063">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-1064">
          <h2 className="text-2xl font-bold text-gray-900" data-id="element-1065">
            {isRTL ? 'المستخدمين' : 'Users'}
          </h2>
          <div className="flex items-center gap-3 w-full sm:w-auto" data-id="element-1066">
            <div className="relative flex-1 sm:w-64" data-id="element-1067">
              <Search className="absolute top-2.5 left-3 w-5 h-5 text-gray-400" data-id="element-1068" />
              <input type="text" placeholder={isRTL ? 'بحث عن مستخدم...' : 'Search users...'} value={userSearch} onChange={e => setUserSearch(e.target.value)} className="w-full pl-10 p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4f1964] outline-none" data-id="element-1069" />
            </div>
            <Button variant="outline" onClick={handleExportUsers} className="flex items-center gap-2 whitespace-nowrap" data-id="element-1070">
              <Download className="w-4 h-4" data-id="element-1071" />
              <span className="hidden sm:inline" data-id="element-1072">
                {isRTL ? 'تصدير' : 'Export'}
              </span>
            </Button>
            <div className="relative" data-id="element-1073">
              <input type="file" accept=".csv" onChange={handleImportUsers} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" disabled={isImportingUsers} data-id="element-1074" />
              <Button variant="outline" className="flex items-center gap-2 whitespace-nowrap" disabled={isImportingUsers} data-id="element-1075">
                {isImportingUsers ? <div className="w-4 h-4 border-2 border-gray-400/30 border-t-gray-400 rounded-full animate-spin" data-id="element-1076" /> : <Upload className="w-4 h-4" data-id="element-1077" />}
                <span className="hidden sm:inline" data-id="element-1078">
                  {isRTL ? 'استيراد' : 'Import'}
                </span>
              </Button>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-1079">
          <div className="overflow-x-auto" data-id="element-1080">
            <table className="w-full text-left border-collapse" data-id="element-1081">
              <thead data-id="element-1082">
                <tr className="bg-gray-50 border-b border-gray-200 text-sm text-gray-500" data-id="element-1083">
                  <th className="p-4 font-medium" data-id="element-1084">
                    {isRTL ? 'الاسم' : 'Name'}
                  </th>
                  <th className="p-4 font-medium" data-id="element-1085">
                    {isRTL ? 'البريد' : 'Email'}
                  </th>
                  <th className="p-4 font-medium" data-id="element-1086">
                    {isRTL ? 'الهاتف' : 'Phone'}
                  </th>
                  <th className="p-4 font-medium" data-id="element-1087">
                    {isRTL ? 'الخطة' : 'Plan'}
                  </th>
                  <th className="p-4 font-medium" data-id="element-1088">
                    {isRTL ? 'الحالة' : 'Status'}
                  </th>
                  <th className="p-4 font-medium" data-id="element-1089">
                    {isRTL ? 'تاريخ التسجيل' : 'Joined'}
                  </th>
                  <th className="p-4 font-medium" data-id="element-1090">
                    {isRTL ? 'إجراءات' : 'Actions'}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100" data-id="element-1091">
                {filteredUsers.map(u => <tr key={u.id} className="hover:bg-gray-50" data-id="element-1092">
                    <td className="p-4 text-sm font-medium text-gray-900" data-id="element-1093">
                      {u.name}
                      {u.brand_name && <span className="block text-xs text-gray-500 font-normal" data-id="element-1094">
                          {u.brand_name}
                        </span>}
                    </td>
                    <td className="p-4 text-sm text-gray-600" data-id="element-1095">{u.email}</td>
                    <td className="p-4 text-sm text-gray-600" dir="ltr" data-id="element-1096">
                      {u.phone || '-'}
                    </td>
                    <td className="p-4" data-id="element-1097">{getPlanBadge(u.plan)}</td>
                    <td className="p-4" data-id="element-1098">
                      {u.is_account_suspended ? <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800" data-id="element-1099">
                          Suspended
                        </span> : <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-1100">
                          Active
                        </span>}
                    </td>
                    <td className="p-4 text-sm text-gray-500" data-id="element-1101">
                      {new Date(u.created_at!).toLocaleDateString()}
                    </td>
                    <td className="p-4" data-id="element-1102">
                      <Button onClick={() => loadUserDetails(u)} variant="outline" size="sm" className="text-[#4f1964] border-[#4f1964] hover:bg-[#4f1964] hover:text-white flex items-center gap-1.5" data-id="element-1103">
                        <Eye className="w-4 h-4" data-id="element-1104" />
                        {isRTL ? 'إدارة' : 'Manage'}
                      </Button>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </div>
      </div>;
  };
  const renderTickets = () => {
    if (selectedTicket) {
      return <div className="space-y-6 max-w-4xl mx-auto" data-id="element-1105">
          <button onClick={() => setSelectedTicket(null)} className="text-gray-500 hover:text-[#4f1964] flex items-center gap-2 text-sm font-medium" data-id="element-1106">
            <X className="w-4 h-4" data-id="element-1107" />
            {isRTL ? 'العودة للقائمة' : 'Back to list'}
          </button>

          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-1108">
            <div className="p-6 border-b border-gray-100" data-id="element-1109">
              <div className="flex justify-between items-start mb-4" data-id="element-1110">
                <div data-id="element-1111">
                  <h2 className="text-xl font-bold text-gray-900 mb-1" data-id="element-1112">
                    {selectedTicket.subject}
                  </h2>
                  <p className="text-sm text-gray-500" data-id="element-1113">
                    {isRTL ? 'بواسطة:' : 'By:'} {selectedTicket.user_email}
                  </p>
                </div>
                <div className="flex items-center gap-3" data-id="element-1114">
                  {getStatusBadge(selectedTicket.status)}
                  <select value={selectedTicket.status} onChange={e => handleUpdateTicketStatus(e.target.value as any)} className="text-sm border border-gray-300 rounded-lg p-1.5 focus:ring-2 focus:ring-[#4f1964] outline-none" data-id="element-1115">
                    <option value="open" data-id="element-1116">Open</option>
                    <option value="in_progress" data-id="element-1117">In Progress</option>
                    <option value="resolved" data-id="element-1118">Resolved</option>
                    <option value="closed" data-id="element-1119">Closed</option>
                  </select>
                </div>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg text-gray-700 whitespace-pre-wrap" data-id="element-1120">
                {renderMessageContent(selectedTicket.message)}
              </div>
            </div>

            <div className="p-6 bg-gray-50 space-y-4" data-id="element-1121">
              <h3 className="font-bold text-gray-900" data-id="element-1122">
                {isRTL ? 'الردود' : 'Replies'}
              </h3>

              {ticketReplies.length === 0 ? <p className="text-sm text-gray-500 text-center py-4" data-id="element-1123">
                  {isRTL ? 'لا توجد ردود بعد' : 'No replies yet'}
                </p> : <div className="space-y-4" data-id="element-1124">
                  {ticketReplies.map(reply => <div key={reply.id} className={`flex ${reply.from_type === 'admin' ? 'justify-end' : 'justify-start'}`} data-id="element-1125">
                      <div className={`max-w-[80%] rounded-2xl p-4 ${reply.from_type === 'admin' ? 'bg-[#4f1964] text-white rounded-tr-none' : 'bg-white border border-gray-200 text-gray-800 rounded-tl-none'}`} data-id="element-1126">
                        <div className="flex items-center gap-2 mb-1 opacity-80 text-xs" data-id="element-1127">
                          <span className="font-bold" data-id="element-1128">{reply.from_name}</span>
                          <span data-id="element-1129">•</span>
                          <span data-id="element-1130">
                            {new Date(reply.created_at!).toLocaleString()}
                          </span>
                        </div>
                        {renderMessageContent(reply.message)}
                      </div>
                    </div>)}
                </div>}

              {selectedTicket.status !== 'closed' && selectedTicket.status !== 'resolved' ? <form onSubmit={handleReplyTicket} className="mt-6" data-id="element-1131">
                  <div className="relative" data-id="element-1132">
                    <textarea value={replyMessage} onChange={e => setReplyMessage(e.target.value)} placeholder={isRTL ? 'اكتب ردك هنا...' : 'Type your reply...'} className="w-full p-3 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4f1964] outline-none resize-none mb-3" rows={3} data-id="element-1133" />
                    <div className="absolute top-3 right-3" data-id="element-1134">
                      <label className="cursor-pointer text-gray-400 hover:text-[#4f1964] transition-colors p-1 block" data-id="element-1135">
                        <input type="file" accept="image/*" className="hidden" onChange={handleFileChange} data-id="element-1136" />
                        <Paperclip className="w-5 h-5" data-id="element-1137" />
                      </label>
                    </div>
                  </div>

                  {attachmentBase64 && <div className="mb-4 relative inline-block" data-id="element-1138">
                      <img src={attachmentBase64} alt="Preview" className="h-20 w-auto rounded border border-gray-200 object-cover" data-id="element-1139" />
                      <button type="button" onClick={() => setAttachmentBase64(null)} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-0.5 hover:bg-red-600" data-id="element-1140">
                        <X className="w-3 h-3" data-id="element-1141" />
                      </button>
                    </div>}

                  <div className="flex justify-end" data-id="element-1142">
                    <Button type="submit" className="bg-[#4f1964] text-white hover:bg-[#3d1350] flex items-center gap-2" disabled={isReplying || !replyMessage.trim() && !attachmentBase64} data-id="element-1143">
                      {isReplying ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-1144" /> : <Send className="w-4 h-4" data-id="element-1145" />}
                      {isRTL ? 'إرسال الرد' : 'Send Reply'}
                    </Button>
                  </div>
                </form> : <div className="mt-6 p-4 bg-gray-100 rounded-lg text-center text-sm text-gray-500" data-id="element-1146">
                  {isRTL ? 'هذه التذكرة مغلقة ولا يمكن الرد عليها' : 'This ticket is closed and cannot be replied to'}
                </div>}
            </div>
          </div>
        </div>;
    }
    const filteredTickets = ticketStatusFilter === 'all' ? tickets : tickets.filter(t => t.status === ticketStatusFilter);
    return <div className="space-y-6" data-id="element-1147">
        <div className="flex justify-between items-center" data-id="element-1148">
          <h2 className="text-2xl font-bold text-gray-900" data-id="element-1149">
            {isRTL ? 'تذاكر الدعم' : 'Support Tickets'}
          </h2>
          <select value={ticketStatusFilter} onChange={e => setTicketStatusFilter(e.target.value)} className="border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-[#4f1964] outline-none" data-id="element-1150">
            <option value="all" data-id="element-1151">{isRTL ? 'الكل' : 'All'}</option>
            <option value="open" data-id="element-1152">{isRTL ? 'مفتوحة' : 'Open'}</option>
            <option value="in_progress" data-id="element-1153">
              {isRTL ? 'قيد المعالجة' : 'In Progress'}
            </option>
            <option value="resolved" data-id="element-1154">{isRTL ? 'تم الحل' : 'Resolved'}</option>
            <option value="closed" data-id="element-1155">{isRTL ? 'مغلقة' : 'Closed'}</option>
          </select>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-1156">
          {filteredTickets.length === 0 ? <div className="p-12 text-center text-gray-500" data-id="element-1157">
              {isRTL ? 'لا توجد تذاكر' : 'No tickets found'}
            </div> : <div className="divide-y divide-gray-100" data-id="element-1158">
              {filteredTickets.map(ticket => <div key={ticket.id} onClick={() => handleTicketClick(ticket)} className="p-5 hover:bg-gray-50 transition-colors cursor-pointer" data-id="element-1159">
                  <div className="flex justify-between items-start gap-4" data-id="element-1160">
                    <div className="flex-1 min-w-0" data-id="element-1161">
                      <div className="flex items-center gap-3 mb-1" data-id="element-1162">
                        <h4 className="text-base font-semibold text-gray-900 truncate" data-id="element-1163">
                          {ticket.subject}
                        </h4>
                        <span className="text-xs text-gray-500" data-id="element-1164">
                          {ticket.user_email}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 line-clamp-1 mb-2" data-id="element-1165">
                        {ticket.message.split('[ATTACHMENT:')[0].trim() || (isRTL ? 'مرفق' : 'Attachment')}
                      </p>
                      <div className="text-xs text-gray-400" data-id="element-1166">
                        {new Date(ticket.created_at!).toLocaleString()}
                      </div>
                    </div>
                    <div className="flex-shrink-0" data-id="element-1167">
                      {getStatusBadge(ticket.status)}
                    </div>
                  </div>
                </div>)}
            </div>}
        </div>
      </div>;
  };
  const handleAddStaff = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!staffName || !staffEmail) {
      showToast('error', isRTL ? 'يرجى ملء جميع الحقول' : 'Please fill all fields');
      return;
    }
    setIsAddingStaff(true);
    try {
      // Staff account must first be created in Supabase Auth dashboard
      // This only adds the metadata entry to admin_staff table
      const newStaff = await dbAdminStaff.create({
        email: staffEmail.toLowerCase().trim(),
        name: staffName,
        password: '',
        role: staffRole,
        permissions: staffPermissions,
        is_active: true
      });
      if (newStaff) {
        setStaff([newStaff, ...staff]);
        setIsStaffModalOpen(false);
        setStaffName('');
        setStaffEmail('');
        setStaffRole('support');
        setStaffPermissions([]);
        showToast('success', isRTL ? 'تم إضافة بيانات الموظف. يجب إنشاء حساب المصادقة من لوحة Supabase Auth.' : 'Staff metadata added. You must create their auth account in Supabase Auth dashboard.');
      } else {
        showToast('error', isRTL ? 'فشل إضافة الموظف' : 'Failed to add staff');
      }
    } catch (error) {
      console.error('Add staff error:', error);
      showToast('error', isRTL ? 'حدث خطأ' : 'An error occurred');
    } finally {
      setIsAddingStaff(false);
    }
  };
  const handleDeleteStaff = async (id: string, email: string) => {
    if (email.toLowerCase() === SUPER_ADMIN_EMAIL.toLowerCase()) {
      showToast('error', isRTL ? 'لا يمكن حذف المدير الأساسي' : 'Cannot delete super admin');
      return;
    }
    if (!confirm(isRTL ? 'هل أنت متأكد من حذف هذا الموظف؟' : 'Delete this staff member?')) return;
    try {
      const success = await dbAdminStaff.delete(id);
      if (success) {
        setStaff(staff.filter(s => s.id !== id));
        showToast('success', isRTL ? 'تم حذف الموظف' : 'Staff member deleted');
      }
    } catch (error) {
      console.error('Delete staff error:', error);
    }
  };
  const handleToggleStaffActive = async (id: string, currentActive: boolean) => {
    try {
      const success = await dbAdminStaff.update(id, {
        is_active: !currentActive
      } as any);
      if (success) {
        setStaff(staff.map(s => s.id === id ? {
          ...s,
          is_active: !currentActive
        } : s));
        showToast('success', isRTL ? 'تم تحديث الحالة' : 'Status updated');
      }
    } catch (error) {
      console.error('Toggle staff error:', error);
    }
  };
  const toggleStaffPermission = (perm: string) => {
    setStaffPermissions(prev => prev.includes(perm) ? prev.filter(p => p !== perm) : [...prev, perm]);
  };
  const ALL_PERMISSIONS = [{
    id: 'view_users',
    label: isRTL ? 'عرض المستخدمين' : 'View Users'
  }, {
    id: 'manage_tickets',
    label: isRTL ? 'إدارة التذاكر' : 'Manage Tickets'
  }, {
    id: 'manage_staff',
    label: isRTL ? 'إدارة الموظفين' : 'Manage Staff'
  }, {
    id: 'send_notifications',
    label: isRTL ? 'إرسال إشعارات' : 'Send Notifications'
  }];
  const renderStaff = () => {
    return <div className="space-y-6" data-id="element-1168">
        <div className="flex justify-between items-center" data-id="element-1169">
          <h2 className="text-2xl font-bold text-gray-900" data-id="element-1170">
            {isRTL ? 'الموظفين' : 'Staff Management'}
          </h2>
          <Button onClick={() => setIsStaffModalOpen(true)} className="bg-[#4f1964] text-white hover:bg-[#3d1350] flex items-center gap-2" data-id="element-1171">
            <Plus className="w-4 h-4" data-id="element-1172" />
            {isRTL ? 'إضافة موظف' : 'Add Staff'}
          </Button>
        </div>

        {/* Super Admin Card */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-1173">
          <div className="p-5 flex items-center justify-between border-b border-gray-100 bg-purple-50/50" data-id="element-1174">
            <div className="flex items-center gap-4" data-id="element-1175">
              <div className="w-10 h-10 rounded-full bg-[#4f1964] flex items-center justify-center text-white font-bold" data-id="element-1176">
                W
              </div>
              <div data-id="element-1177">
                <h4 className="font-semibold text-gray-900" data-id="element-1178">Wael Eldeniney</h4>
                <p className="text-sm text-gray-500" data-id="element-1179">{SUPER_ADMIN_EMAIL}</p>
              </div>
            </div>
            <div className="flex items-center gap-3" data-id="element-1180">
              <span className="px-3 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800" data-id="element-1181">
                Super Admin
              </span>
              <span className="px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-1182">
                {isRTL ? 'نشط' : 'Active'}
              </span>
            </div>
          </div>

          {/* Staff List */}
          {staff.length === 0 ? <div className="p-8 text-center text-gray-500" data-id="element-1183">
              <Shield className="w-10 h-10 text-gray-300 mx-auto mb-3" data-id="element-1184" />
              <p data-id="element-1185">
                {isRTL ? 'لا يوجد موظفين مضافين بعد' : 'No staff members added yet'}
              </p>
            </div> : <div className="divide-y divide-gray-100" data-id="element-1186">
              {staff.map(s => <div key={s.id} className="p-5 flex items-center justify-between hover:bg-gray-50" data-id="element-1187">
                  <div className="flex items-center gap-4" data-id="element-1188">
                    <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 font-bold" data-id="element-1189">
                      {s.name.charAt(0)}
                    </div>
                    <div data-id="element-1190">
                      <h4 className="font-semibold text-gray-900" data-id="element-1191">{s.name}</h4>
                      <p className="text-sm text-gray-500" data-id="element-1192">{s.email}</p>
                      {s.permissions && s.permissions.length > 0 && <div className="flex flex-wrap gap-1 mt-1" data-id="element-1193">
                          {s.permissions.map(p => <span key={p} className="px-1.5 py-0.5 rounded text-[10px] bg-gray-100 text-gray-600" data-id="element-1194">
                              {p}
                            </span>)}
                        </div>}
                    </div>
                  </div>
                  <div className="flex items-center gap-3" data-id="element-1195">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium capitalize ${s.role === 'admin' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'}`} data-id="element-1196">
                      {s.role}
                    </span>
                    <button onClick={() => handleToggleStaffActive(s.id!, s.is_active)} className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${s.is_active ? 'bg-green-100 text-green-800 hover:bg-green-200' : 'bg-red-100 text-red-800 hover:bg-red-200'}`} data-id="element-1197">
                      {s.is_active ? isRTL ? 'نشط' : 'Active' : isRTL ? 'معطل' : 'Inactive'}
                    </button>
                    <button onClick={() => handleDeleteStaff(s.id!, s.email)} className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors" data-id="element-1198">
                      <Trash2 className="w-4 h-4" data-id="element-1199" />
                    </button>
                  </div>
                </div>)}
            </div>}
        </div>

        {/* Add Staff Modal */}
        <AnimatePresence data-id="element-1200">
          {isStaffModalOpen && <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => !isAddingStaff && setIsStaffModalOpen(false)} data-id="element-1201">
              <motion.div initial={{
            scale: 0.95,
            opacity: 0
          }} animate={{
            scale: 1,
            opacity: 1
          }} exit={{
            scale: 0.95,
            opacity: 0
          }} onClick={e => e.stopPropagation()} className="bg-white rounded-2xl shadow-xl w-full max-w-lg overflow-hidden" data-id="element-1202">
                <div className="p-6 border-b border-gray-100 flex justify-between items-center" data-id="element-1203">
                  <h3 className="text-lg font-bold text-gray-900" data-id="element-1204">
                    {isRTL ? 'إضافة موظف جديد' : 'Add New Staff'}
                  </h3>
                  <button onClick={() => !isAddingStaff && setIsStaffModalOpen(false)} className="text-gray-400 hover:text-gray-600" disabled={isAddingStaff} data-id="element-1205">
                    <X className="w-5 h-5" data-id="element-1206" />
                  </button>
                </div>
                <form onSubmit={handleAddStaff} className="p-6 space-y-5" data-id="element-1207">
                  <div data-id="element-1208">
                    <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-1209">
                      {isRTL ? 'الاسم' : 'Name'}
                    </label>
                    <input type="text" value={staffName} onChange={e => setStaffName(e.target.value)} className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4f1964] outline-none" required disabled={isAddingStaff} data-id="element-1210" />
                  </div>
                  <div data-id="element-1211">
                    <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-1212">
                      {isRTL ? 'البريد الإلكتروني' : 'Email'}
                    </label>
                    <input type="email" value={staffEmail} onChange={e => setStaffEmail(e.target.value)} className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4f1964] outline-none" required dir="ltr" disabled={isAddingStaff} data-id="element-1213" />
                  </div>
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg" data-id="element-1214">
                    <p className="text-sm text-amber-800" data-id="element-1215">
                      {isRTL ? '⚠️ يجب إنشاء حساب المصادقة أولاً من لوحة Supabase Auth Dashboard، ثم إضافة البيانات هنا.' : '⚠️ You must first create the auth account in Supabase Auth Dashboard, then add metadata here.'}
                    </p>
                  </div>
                  <div data-id="element-1216">
                    <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-1217">
                      {isRTL ? 'الدور' : 'Role'}
                    </label>
                    <div className="grid grid-cols-2 gap-3" data-id="element-1218">
                      <button type="button" onClick={() => setStaffRole('support')} className={`py-2.5 px-3 rounded-lg text-sm font-medium border transition-all ${staffRole === 'support' ? 'bg-blue-50 border-blue-200 text-blue-700' : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'}`} data-id="element-1219">
                        {isRTL ? 'دعم فني' : 'Support'}
                      </button>
                      <button type="button" onClick={() => setStaffRole('admin')} className={`py-2.5 px-3 rounded-lg text-sm font-medium border transition-all ${staffRole === 'admin' ? 'bg-purple-50 border-purple-200 text-purple-700' : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'}`} data-id="element-1220">
                        {isRTL ? 'مدير' : 'Admin'}
                      </button>
                    </div>
                  </div>
                  <div data-id="element-1221">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1222">
                      {isRTL ? 'الصلاحيات' : 'Permissions'}
                    </label>
                    <div className="space-y-2" data-id="element-1223">
                      {ALL_PERMISSIONS.map(perm => <label key={perm.id} className="flex items-center gap-3 p-2.5 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer transition-colors" data-id="element-1224">
                          <input type="checkbox" checked={staffPermissions.includes(perm.id)} onChange={() => toggleStaffPermission(perm.id)} className="w-4 h-4 text-[#4f1964] rounded border-gray-300 focus:ring-[#4f1964]" data-id="element-1225" />
                          <span className="text-sm text-gray-700" data-id="element-1226">
                            {perm.label}
                          </span>
                        </label>)}
                    </div>
                  </div>
                  <div className="pt-2 flex gap-3" data-id="element-1227">
                    <Button type="button" variant="outline" onClick={() => setIsStaffModalOpen(false)} className="flex-1" disabled={isAddingStaff} data-id="element-1228">
                      {isRTL ? 'إلغاء' : 'Cancel'}
                    </Button>
                    <Button type="submit" className="flex-1 bg-[#4f1964] text-white hover:bg-[#3d1350] flex items-center justify-center gap-2" disabled={isAddingStaff} data-id="element-1229">
                      {isAddingStaff ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-1230" /> : <>
                          <Plus className="w-4 h-4" data-id="element-1231" />
                          {isRTL ? 'إضافة' : 'Add'}
                        </>}
                    </Button>
                  </div>
                </form>
              </motion.div>
            </motion.div>}
        </AnimatePresence>
      </div>;
  };
  const renderNotifications = () => {
    return <div className="space-y-6 max-w-2xl mx-auto" data-id="element-1232">
        <h2 className="text-2xl font-bold text-gray-900" data-id="element-1233">
          {isRTL ? 'إرسال إشعار' : 'Send Notification'}
        </h2>

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden p-6" data-id="element-1234">
          <form onSubmit={handleSendNotification} className="space-y-5" data-id="element-1235">
            <div data-id="element-1236">
              <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-1237">
                {isRTL ? 'المستلم (البريد الإلكتروني)' : 'Recipient (Email)'}
              </label>
              <select value={notifEmail} onChange={e => setNotifEmail(e.target.value)} className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4f1964] outline-none" required data-id="element-1238">
                <option value="" data-id="element-1239">
                  {isRTL ? 'اختر مستخدم...' : 'Select user...'}
                </option>
                <option value="all" className="font-bold text-[#4f1964]" data-id="element-1240">
                  {isRTL ? 'جميع المستخدمين (All Users)' : 'All Users'}
                </option>
                {users.map(u => <option key={u.id} value={u.email} data-id="element-1241">
                    {u.name} ({u.email})
                  </option>)}
              </select>
            </div>

            <div data-id="element-1242">
              <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-1243">
                {isRTL ? 'نوع الإشعار' : 'Notification Type'}
              </label>
              <div className="grid grid-cols-3 gap-3" data-id="element-1244">
                {[{
                id: 'info',
                label: isRTL ? 'معلومة' : 'Info',
                color: 'blue'
              }, {
                id: 'warning',
                label: isRTL ? 'تنبيه' : 'Warning',
                color: 'yellow'
              }, {
                id: 'success',
                label: isRTL ? 'نجاح' : 'Success',
                color: 'green'
              }].map(t => <button key={t.id} type="button" onClick={() => setNotifType(t.id as any)} className={`py-2 px-3 rounded-lg text-sm font-medium border transition-all ${notifType === t.id ? t.color === 'yellow' ? 'bg-yellow-50 border-yellow-200 text-yellow-700' : t.color === 'green' ? 'bg-green-50 border-green-200 text-green-700' : 'bg-blue-50 border-blue-200 text-blue-700' : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'}`} data-id="element-1245">
                    {t.label}
                  </button>)}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-1246">
              <div className="space-y-4" data-id="element-1247">
                <h4 className="font-medium text-gray-900 border-b pb-2" data-id="element-1248">
                  English
                </h4>
                <div data-id="element-1249">
                  <label className="block text-xs text-gray-500 mb-1" data-id="element-1250">
                    Title
                  </label>
                  <input type="text" value={notifTitleEn} onChange={e => setNotifTitleEn(e.target.value)} className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4f1964] outline-none text-sm" required dir="ltr" data-id="element-1251" />
                </div>
                <div data-id="element-1252">
                  <label className="block text-xs text-gray-500 mb-1" data-id="element-1253">
                    Message
                  </label>
                  <textarea value={notifMessageEn} onChange={e => setNotifMessageEn(e.target.value)} rows={4} className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4f1964] outline-none text-sm resize-none" required dir="ltr" data-id="element-1254" />
                </div>
              </div>

              <div className="space-y-4" data-id="element-1255">
                <h4 className="font-medium text-gray-900 border-b pb-2" data-id="element-1256">
                  العربية
                </h4>
                <div data-id="element-1257">
                  <label className="block text-xs text-gray-500 mb-1" data-id="element-1258">
                    العنوان
                  </label>
                  <input type="text" value={notifTitleAr} onChange={e => setNotifTitleAr(e.target.value)} className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4f1964] outline-none text-sm" required dir="rtl" data-id="element-1259" />
                </div>
                <div data-id="element-1260">
                  <label className="block text-xs text-gray-500 mb-1" data-id="element-1261">
                    الرسالة
                  </label>
                  <textarea value={notifMessageAr} onChange={e => setNotifMessageAr(e.target.value)} rows={4} className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4f1964] outline-none text-sm resize-none" required dir="rtl" data-id="element-1262" />
                </div>
              </div>
            </div>

            <div className="pt-4 border-t border-gray-100 flex justify-end" data-id="element-1263">
              <Button type="submit" className="bg-[#4f1964] text-white hover:bg-[#3d1350] flex items-center gap-2" disabled={isSendingNotif} data-id="element-1264">
                {isSendingNotif ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-1265" /> : <Send className="w-4 h-4" data-id="element-1266" />}
                {isRTL ? 'إرسال الإشعار' : 'Send Notification'}
              </Button>
            </div>
          </form>
        </div>
      </div>;
  };
  return <div className="min-h-screen bg-gray-50 flex" dir={isRTL ? 'rtl' : 'ltr'} data-id="element-1267">
      {/* Mobile Menu Overlay */}
      <AnimatePresence data-id="element-1268">
        {mobileMenuOpen && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={() => setMobileMenuOpen(false)} className="fixed inset-0 bg-black/50 z-40 md:hidden" data-id="element-1269" />}
      </AnimatePresence>

      {/* Sidebar */}
      <motion.aside className={`fixed md:static inset-y-0 ${isRTL ? 'right-0' : 'left-0'} z-50 w-64 bg-[#4f1964] text-white transform transition-transform duration-300 ${mobileMenuOpen ? 'translate-x-0' : isRTL ? 'translate-x-full' : '-translate-x-full'} md:translate-x-0 flex flex-col`} data-id="element-1270">
        <div className="p-5 border-b border-purple-800 flex items-center justify-between" data-id="element-1271">
          <div className="flex items-center gap-3" data-id="element-1272">
            <div className="w-9 h-9 rounded-lg bg-white/20 flex items-center justify-center text-white" data-id="element-1273">
              <Shield className="w-5 h-5" data-id="element-1274" />
            </div>
            <span className="font-bold text-lg" data-id="element-1275">Admin Panel</span>
          </div>
          <button onClick={() => setMobileMenuOpen(false)} className="md:hidden text-white" data-id="element-1276">
            <X className="w-6 h-6" data-id="element-1277" />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1" data-id="element-1278">
          {sidebarItems.map(item => <button key={item.id} onClick={() => {
          setActiveSection(item.id as AdminSection);
          setMobileMenuOpen(false);
        }} className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${activeSection === item.id ? 'bg-white/15 text-white font-medium' : 'text-purple-200 hover:bg-white/5 hover:text-white'}`} data-id="element-1279">
              <item.icon className="w-5 h-5" data-id="element-1280" />
              <span className="flex-1 text-start" data-id="element-1281">{item.label}</span>
            </button>)}
        </nav>

        <div className="p-4 border-t border-purple-800" data-id="element-1282">
          <div className="flex items-center gap-3 mb-4 px-2" data-id="element-1283">
            <div className="w-8 h-8 rounded-full bg-purple-500 flex items-center justify-center text-white font-bold text-sm" data-id="element-1284">
              {adminUser.name.charAt(0)}
            </div>
            <div className="flex-1 min-w-0" data-id="element-1285">
              <p className="text-sm font-medium text-white truncate" data-id="element-1286">
                {adminUser.name}
              </p>
              <p className="text-xs text-purple-300 truncate capitalize" data-id="element-1287">
                {adminUser.role.replace('_', ' ')}
              </p>
            </div>
          </div>
          <button onClick={handleLogout} className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-red-300 hover:bg-red-500/10 hover:text-red-200 transition-colors" data-id="element-1288">
            <LogOut className="w-5 h-5" data-id="element-1289" />
            <span className="text-sm font-medium" data-id="element-1290">
              {isRTL ? 'تسجيل الخروج' : 'Logout'}
            </span>
          </button>
        </div>
      </motion.aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden" data-id="element-1291">
        <header className="bg-white border-b border-gray-200 h-16 flex items-center justify-between px-4 sm:px-6 lg:px-8" data-id="element-1292">
          <div className="flex items-center gap-4" data-id="element-1293">
            <button onClick={() => setMobileMenuOpen(true)} className="md:hidden text-gray-500" data-id="element-1294">
              <Menu className="w-6 h-6" data-id="element-1295" />
            </button>
            <h1 className="text-xl font-bold text-gray-900" data-id="element-1296">
              {sidebarItems.find(i => i.id === activeSection)?.label}
            </h1>
          </div>
          <div className="flex items-center gap-3" data-id="element-1297">
            {isLoadingData && <div className="flex items-center gap-2 text-sm text-gray-500" data-id="element-1298">
                <div className="w-4 h-4 border-2 border-[#4f1964]/30 border-t-[#4f1964] rounded-full animate-spin" data-id="element-1299" />
                {isRTL ? 'تحديث...' : 'Syncing...'}
              </div>}
            <Button variant="outline" size="sm" onClick={loadDashboardData} disabled={isLoadingData} data-id="element-1300">
              {isRTL ? 'تحديث البيانات' : 'Refresh'}
            </Button>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8" data-id="element-1301">
          <AnimatePresence mode="wait" data-id="element-1302">
            <motion.div key={activeSection} initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -10
          }} transition={{
            duration: 0.2
          }} data-id="element-1303">
              {activeSection === 'overview' && renderOverview()}
              {activeSection === 'users' && renderUsers()}
              {activeSection === 'tickets' && renderTickets()}
              {activeSection === 'staff' && renderStaff()}
              {activeSection === 'notifications' && renderNotifications()}
            </motion.div>
          </AnimatePresence>
        </main>
      </div>
    </div>;
}